import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty, fmtMoney } from './InventoryShared';

export default function InventoryProducts() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [lowStock, setLowStock] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/products?per_page=50`;
      if (status) url += `&status=${status}`;
      if (categoryId) url += `&category_id=${categoryId}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (lowStock) url += `&low_stock=1`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, categoryId, search, lowStock]);

  const fetchAux = useCallback(async () => {
    try {
      const [cRes, wRes] = await Promise.all([
        fetch(`${API}/companies/${companyId}/inventory/categories`, { headers: getAuthHeaders() }),
        fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
      ]);
      setCategories(await cRes.json());
      setWarehouses(await wRes.json());
    } catch (e) { console.error(e); }
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const viewProduct = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/products/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    const res = await fetch(`${API}/companies/${companyId}/inventory/products/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏷️ Products & Stock Catalog</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Product</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search name/SKU/barcode..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={{ ...inputStyle, width: 150 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="incomplete">Incomplete</option>
        </select>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={lowStock} onChange={e => setLowStock(e.target.checked)} /> Low stock only
        </label>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Category', 'Unit', 'On Hand', 'Available', 'Reserved', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No products</td></tr>}
              {list.map(p => (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{p.product_code}</td>
                  <td style={tdStyle}><a href="#view" onClick={e => { e.preventDefault(); viewProduct(p.id); }} style={{ color: '#3b82f6' }}>{p.name}</a></td>
                  <td style={tdStyle}>{p.category?.name || '—'}</td>
                  <td style={tdStyle}>{p.unit}</td>
                  <td style={tdStyle}>{fmtQty(p.total_on_hand)}</td>
                  <td style={tdStyle}>{fmtQty(p.available)}</td>
                  <td style={tdStyle}>{fmtQty(p.reserved)}</td>
                  <td style={tdStyle}><Badge value={p.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(p)}>Edit</button>
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(p.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ProductModal companyId={companyId} product={editing} categories={categories} warehouses={warehouses}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}
      {viewing && <ProductDetail product={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function ProductModal({ companyId, product, categories, warehouses, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || '', description: product.description || '', category_id: product.category_id || '',
    sku: product.sku || '', barcode: product.barcode || '', unit: product.unit || 'pcs',
    current_cost: product.current_cost || 0, selling_price: product.selling_price || 0,
    minimum_stock: product.minimum_stock || 0, reorder_level: product.reorder_level || 0, maximum_stock: product.maximum_stock || 0,
    serial_tracked: product.serial_tracked || false, batch_tracked: product.batch_tracked || false,
    status: product.status || 'active',
    opening_qty: '', opening_warehouse_id: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/inventory/products` : `${API}/companies/${companyId}/inventory/products/${product.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Product' : 'Edit Product'}</h3>
        <FormRow label="Name *"><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormRow>
        <FormRow label="Description"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></FormRow>
        <FormRow label="Category">
          <select style={inputStyle} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
            <option value="">— None —</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormRow>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="SKU"><input style={inputStyle} value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Barcode"><input style={inputStyle} value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Unit"><input style={inputStyle} value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} /></FormRow></div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Cost"><input type="number" style={inputStyle} value={form.current_cost} onChange={e => setForm({ ...form, current_cost: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Selling Price"><input type="number" style={inputStyle} value={form.selling_price} onChange={e => setForm({ ...form, selling_price: e.target.value })} /></FormRow></div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Min Stock"><input type="number" style={inputStyle} value={form.minimum_stock} onChange={e => setForm({ ...form, minimum_stock: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Reorder Level"><input type="number" style={inputStyle} value={form.reorder_level} onChange={e => setForm({ ...form, reorder_level: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Max Stock"><input type="number" style={inputStyle} value={form.maximum_stock} onChange={e => setForm({ ...form, maximum_stock: e.target.value })} /></FormRow></div>
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.serial_tracked} onChange={e => setForm({ ...form, serial_tracked: e.target.checked })} /> Serial Tracked
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.batch_tracked} onChange={e => setForm({ ...form, batch_tracked: e.target.checked })} /> Batch Tracked
          </label>
        </div>
        {isNew && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6, marginTop: 4 }}>OPENING STOCK (optional)</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><FormRow label="Warehouse">
                <select style={inputStyle} value={form.opening_warehouse_id} onChange={e => setForm({ ...form, opening_warehouse_id: e.target.value })}>
                  <option value="">— None —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </FormRow></div>
              <div style={{ flex: 1 }}><FormRow label="Opening Qty"><input type="number" style={inputStyle} value={form.opening_qty} onChange={e => setForm({ ...form, opening_qty: e.target.value })} /></FormRow></div>
            </div>
          </>
        )}
        {!isNew && (
          <FormRow label="Status">
            <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </FormRow>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function ProductDetail({ product, onClose }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{product.name}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div><b>Code:</b> {product.product_code}</div>
          <div><b>SKU:</b> {product.sku || '—'}</div>
          <div><b>On Hand:</b> {fmtQty(product.total_on_hand)}</div>
          <div><b>Available:</b> {fmtQty(product.available)}</div>
          <div><b>Reserved:</b> {fmtQty(product.reserved)}</div>
          <div><b>Cost:</b> {fmtMoney(product.current_cost)}</div>
        </div>
        <h4>Balances by Location</h4>
        <table style={tableStyle}>
          <thead><tr>{['Warehouse', 'Location', 'On Hand'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(product.balances || []).filter(b => b.on_hand_qty > 0).map(b => (
              <tr key={b.id}>
                <td style={tdStyle}>{b.warehouse?.name}</td>
                <td style={tdStyle}>{b.location?.code || '—'}</td>
                <td style={tdStyle}>{fmtQty(b.on_hand_qty)}</td>
              </tr>
            ))}
            {(product.balances || []).filter(b => b.on_hand_qty > 0).length === 0 && (
              <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No stock</td></tr>
            )}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
